import type { Track } from '../types/domain';
import type { PageSectionLayout } from '../services/sectionLayoutService';
import {
  ANNUAL_WEEKDAY_KEYS,
  isAnnualShortDayTailMemo,
  isFixedProgramColor,
  MAX_ANNUAL_TIMETABLE_SLOTS,
  normalizeColorHexForCompare,
  type AnnualEffectiveCell,
} from './annualTimetable';
import type {
  HoursAdjustChecklistTransfer,
  HoursAdjustPhase2Result,
} from './annualTimetableHoursAdjustPlan';
import { orderedTrackAliasesForGrade } from './trackTimetableAlias';
import { loadTrackProgramModeMap } from './trackProgramModeStorage';
import { loadTrackProgramSyncRepMap } from './trackProgramSyncRepStorage';
import { matchesTrackWildcardSuffix, sanitizeWildcardChar } from './trackWildcard';

export type HoursAdjustChecklistCellUpdate = {
  key: string;
  textValue: string;
  /** 전담·제외·기타 색 제거 → 기본(무색) */
  colorHex: string;
  isBaseExcluded: boolean;
};

export type HoursAdjustChecklistApplyResult = {
  updates: HoursAdjustChecklistCellUpdate[];
  results: Array<{
    transferId: string;
    term: 1 | 2;
    fromTrack: string;
    toTrack: string;
    requested: number;
    applied: number;
  }>;
  appliedCount: number;
  shortfallCount: number;
  /** 옵션3: 「교과」 행사 칸을 부족 편제에 배정한 칸 수 */
  curriculumFillAppliedCount: number;
};

type TrackAliasInfo = {
  matchAliases: string[];
  writeText: string;
};

function weekdayRank(key: string): number {
  const idx = ANNUAL_WEEKDAY_KEYS.findIndex((w) => w.key === key);
  return idx >= 0 ? idx : 99;
}

/** 마지막 주·요일·교시부터 역순 */
function compareCellsReverse(a: AnnualEffectiveCell, b: AnnualEffectiveCell): number {
  if (a.mondayIso !== b.mondayIso) return a.mondayIso < b.mondayIso ? 1 : -1;
  const wd = weekdayRank(b.weekdayKey) - weekdayRank(a.weekdayKey);
  if (wd !== 0) return wd;
  return b.slotIndex - a.slotIndex;
}

function resolveAliasesForTrack(params: {
  workspaceId: string;
  gradeId: string;
  trackName: string;
  tracks: Track[];
}): TrackAliasInfo {
  const { workspaceId, gradeId, trackName, tracks } = params;
  const name = trackName.trim();
  const ordered = orderedTrackAliasesForGrade(workspaceId, gradeId, name, tracks);
  const aliases = ordered.length > 0 ? ordered : [name].filter(Boolean);
  const modeMap = loadTrackProgramModeMap(workspaceId, gradeId);
  const syncRepMap = loadTrackProgramSyncRepMap(workspaceId, gradeId);
  const mode = modeMap[name] ?? 'unified';
  if (mode === 'sync' && aliases.length > 1) {
    const repRaw = (syncRepMap[name] ?? '').trim();
    const rep = repRaw && aliases.includes(repRaw) ? repRaw : aliases[0]!;
    return { matchAliases: rep ? [rep] : [], writeText: rep || name };
  }
  return { matchAliases: aliases, writeText: aliases[0] || name };
}

function cellMatchesTrack(
  cell: AnnualEffectiveCell,
  matchAliases: string[],
  wildcardChar: string,
  trackName: string
): boolean {
  const text = (cell.textValue ?? '').trim();
  if (!text) return false;
  if (matchAliases.includes(text)) return true;
  if (text === trackName.trim()) return true;
  const wildcard = sanitizeWildcardChar(wildcardChar);
  if (wildcard && matchesTrackWildcardSuffix(text, wildcard)) return true;
  return false;
}

/** 고정 팔레트 색·행사 메모 칸은 편제 시수 이동 대상이 아니다. 전담·제외 색 칸은 실수업으로 센다. */
function isOperatingCandidate(
  cell: AnnualEffectiveCell,
  regularColors: string[],
  showSaturday: boolean
): boolean {
  if (!cell.isEditable) return false;
  if (cell.slotKind !== 'class' && cell.slotKind !== 'non_class') return false;
  if (!showSaturday && cell.weekdayKey === 'sat') return false;
  if (cell.slotIndex < 0 || cell.slotIndex >= MAX_ANNUAL_TIMETABLE_SLOTS) return false;
  if (isFixedProgramColor(cell.colorHex, regularColors)) return false;
  const memo = (cell.memo ?? '').trim();
  if (memo && !isAnnualShortDayTailMemo(memo)) return false;
  return true;
}

/** 옵션3: 시간표명이 「교과」인 고정(행사) 칸 — 시수 맞춤 보충용 */
const CURRICULUM_EVENT_CELL_LABEL = '교과';

function isCurriculumFixedEventCandidate(
  cell: AnnualEffectiveCell,
  regularColors: string[],
  showSaturday: boolean
): boolean {
  if (!cell.isEditable) return false;
  if (cell.slotKind !== 'class' && cell.slotKind !== 'non_class') return false;
  if (!showSaturday && cell.weekdayKey === 'sat') return false;
  if (cell.slotIndex < 0 || cell.slotIndex >= MAX_ANNUAL_TIMETABLE_SLOTS) return false;
  if (!isFixedProgramColor(cell.colorHex, regularColors)) return false;
  if ((cell.textValue ?? '').trim() !== CURRICULUM_EVENT_CELL_LABEL) return false;
  const memo = (cell.memo ?? '').trim();
  if (memo && !isAnnualShortDayTailMemo(memo)) return false;
  return true;
}

function buildSkipColorSet(skipColorHexes: string[] | undefined): Set<string> {
  const set = new Set<string>();
  (skipColorHexes ?? []).forEach((hex) => {
    const n = normalizeColorHexForCompare(hex);
    if (n) set.add(n);
  });
  return set;
}

function cellMatchesSkipColor(cell: AnnualEffectiveCell, skipSet: Set<string>): boolean {
  if (skipSet.size === 0) return false;
  const n = normalizeColorHexForCompare(cell.colorHex ?? '');
  return Boolean(n) && skipSet.has(n);
}

/**
 * 자동 반영 결과 → 체크리스트 완료 여부.
 * 체크리스트 id와 같은 transferId 결과가 있으면 그 applied로 판정하고,
 * 없으면 같은 학기·from→to 쌍의 applied를 배분합니다.
 * 요청 시수를 모두 채운 항목만 완료(true).
 */
export function checklistCheckedFromApplyResults(
  transfers: HoursAdjustChecklistTransfer[],
  results: HoursAdjustChecklistApplyResult['results']
): Record<string, boolean> {
  const byId = new Map<string, number>();
  const pool = new Map<string, number>();
  for (const r of results) {
    byId.set(r.transferId, (byId.get(r.transferId) ?? 0) + r.applied);
    const k = `${r.term}\0${r.fromTrack}\0${r.toTrack}`;
    pool.set(k, (pool.get(k) ?? 0) + r.applied);
  }
  const out: Record<string, boolean> = {};
  for (const t of transfers) {
    if (byId.has(t.id)) {
      out[t.id] = t.hours > 0 && (byId.get(t.id) ?? 0) >= t.hours;
      continue;
    }
    const k = `${t.term}\0${t.fromTrack}\0${t.toTrack}`;
    const available = pool.get(k) ?? 0;
    if (t.hours > 0 && available >= t.hours) {
      out[t.id] = true;
      pool.set(k, available - t.hours);
    } else {
      out[t.id] = false;
      if (available > 0) pool.set(k, 0);
    }
  }
  return out;
}

function pairTermTransfers(
  term: 1 | 2,
  deltas: Array<{ trackName: string; delta: number }>
): HoursAdjustChecklistTransfer[] {
  const donors: Array<{ trackName: string; remain: number }> = [];
  const receivers: Array<{ trackName: string; remain: number }> = [];
  for (const d of deltas) {
    if (d.delta < 0) donors.push({ trackName: d.trackName, remain: -d.delta });
    if (d.delta > 0) receivers.push({ trackName: d.trackName, remain: d.delta });
  }
  donors.sort((a, b) => b.remain - a.remain || a.trackName.localeCompare(b.trackName, 'ko'));
  receivers.sort((a, b) => b.remain - a.remain || a.trackName.localeCompare(b.trackName, 'ko'));

  const out: HoursAdjustChecklistTransfer[] = [];
  let di = 0;
  let ri = 0;
  let seq = 0;
  while (di < donors.length && ri < receivers.length) {
    const donor = donors[di]!;
    const receiver = receivers[ri]!;
    const hours = Math.min(donor.remain, receiver.remain);
    if (hours > 0) {
      seq += 1;
      out.push({
        id: `live-t${term}-${seq}-${donor.trackName}-${receiver.trackName}`,
        term,
        fromTrack: donor.trackName,
        toTrack: receiver.trackName,
        hours,
      });
      donor.remain -= hours;
      receiver.remain -= hours;
    }
    if (donor.remain <= 0) di += 1;
    if (receiver.remain <= 0) ri += 1;
  }
  return out;
}

/**
 * 현재 연간시간표 셀을 2차 목표 실수업 시수에 맞추도록 변경안을 만든다.
 * - 학기별 마지막 셀부터 역순으로 from→to 시간표명 교체
 * - 변경 셀은 색을 제거하고 기본(무색)으로 둔다
 * - 고정 프로그램(행사) 색 칸은 이동하지 않는다
 * - skipColorHexes에 해당하는 색 칸은 수정 후보에서 제외(시수 집계에는 포함)
 */
export function planHoursAdjustChecklistCellUpdates(params: {
  workspaceId: string;
  gradeId: string;
  tracks: Track[];
  gradeTrackSections: PageSectionLayout[] | null | undefined;
  cells: AnnualEffectiveCell[];
  phase2: HoursAdjustPhase2Result;
  term1MondayIsos: string[];
  term2MondayIsos: string[];
  showSaturday: boolean;
  regularColors: string[];
  wildcardCharByTrackName?: Record<string, string>;
  /** 자동 조정에서 건드리지 않을 색(전담·기초 제외 등) */
  skipColorHexes?: string[];
  /**
   * 체크리스트 이전 목록. 있으면 이 순서로 반영하고(라이브 여유·부족 한도 내),
   * 남은 시수는 라이브 델타로 추가 이전합니다.
   */
  checklistTransfers?: HoursAdjustChecklistTransfer[];
  /**
   * 옵션3: 시간표명 「교과」인 행사(고정 색) 칸을 부족 편제에 배정해 시수를 맞춘다.
   */
  fillFromCurriculumEventCells?: boolean;
}): HoursAdjustChecklistApplyResult {
  const {
    workspaceId,
    gradeId,
    tracks,
    gradeTrackSections,
    cells,
    phase2,
    term1MondayIsos,
    term2MondayIsos,
    showSaturday,
    regularColors,
    wildcardCharByTrackName = {},
    skipColorHexes,
    checklistTransfers,
    fillFromCurriculumEventCells = false,
  } = params;
  const skipColorSet = buildSkipColorSet(skipColorHexes);

  const term1Set = new Set(term1MondayIsos);
  const term2Set = new Set(term2MondayIsos);

  const wildcardByTrack: Record<string, string> = { ...wildcardCharByTrackName };
  (gradeTrackSections ?? []).forEach((section) => {
    if (!section.gradeIds.includes(gradeId)) return;
    (section.trackColumns ?? []).forEach((column) => {
      const trackName = (column.name ?? '').trim();
      const wildcard = sanitizeWildcardChar(column.wildcardChar ?? '');
      if (trackName && wildcard && !wildcardByTrack[trackName]) {
        wildcardByTrack[trackName] = wildcard;
      }
    });
  });

  const aliasCache = new Map<string, TrackAliasInfo>();
  const getAliasInfo = (trackName: string) => {
    const key = trackName.trim();
    let cached = aliasCache.get(key);
    if (!cached) {
      cached = resolveAliasesForTrack({ workspaceId, gradeId, trackName: key, tracks });
      aliasCache.set(key, cached);
    }
    return cached;
  };

  const trackNames = phase2.rows.map((r) => r.trackName);
  const countByTrackTerm = (term: 1 | 2): Record<string, number> => {
    const mondaySet = term === 1 ? term1Set : term2Set;
    const counts: Record<string, number> = {};
    trackNames.forEach((name) => {
      counts[name] = 0;
    });
    for (const cell of cells) {
      if (cell.gradeId !== gradeId) continue;
      if (!mondaySet.has(cell.mondayIso)) continue;
      if (!isOperatingCandidate(cell, regularColors, showSaturday)) continue;
      for (const trackName of trackNames) {
        const info = getAliasInfo(trackName);
        if (cellMatchesTrack(cell, info.matchAliases, wildcardByTrack[trackName] ?? '', trackName)) {
          counts[trackName] = (counts[trackName] ?? 0) + 1;
          break;
        }
      }
    }
    return counts;
  };

  const currentT1 = countByTrackTerm(1);
  const currentT2 = countByTrackTerm(2);

  const deltasT1 = phase2.rows.map((r) => ({
    trackName: r.trackName,
    delta: r.adjustedTerm1 - (currentT1[r.trackName] ?? 0),
  }));
  const deltasT2 = phase2.rows.map((r) => ({
    trackName: r.trackName,
    delta: r.adjustedTerm2 - (currentT2[r.trackName] ?? 0),
  }));

  const giveRemain = new Map<string, number>();
  const needRemain = new Map<string, number>();
  const seedCapacity = (term: 1 | 2, deltas: Array<{ trackName: string; delta: number }>) => {
    for (const d of deltas) {
      if (d.delta < 0) giveRemain.set(`${term}\0${d.trackName}`, -d.delta);
      if (d.delta > 0) needRemain.set(`${term}\0${d.trackName}`, d.delta);
    }
  };
  seedCapacity(1, deltasT1);
  seedCapacity(2, deltasT2);

  const usedKeys = new Set<string>();
  const updates: HoursAdjustChecklistCellUpdate[] = [];
  const results: HoursAdjustChecklistApplyResult['results'] = [];

  const applyOneTransfer = (
    transfer: HoursAdjustChecklistTransfer,
    hoursBudget: number
  ): number => {
    const mondaySet = transfer.term === 1 ? term1Set : term2Set;
    const fromInfo = getAliasInfo(transfer.fromTrack);
    const toInfo = getAliasInfo(transfer.toTrack);
    const wildcard = wildcardByTrack[transfer.fromTrack.trim()] ?? '';
    const writeText = (toInfo.writeText || transfer.toTrack).trim();
    if (!writeText || hoursBudget <= 0) return 0;

    const candidates = cells
      .filter((cell) => {
        if (cell.gradeId !== gradeId) return false;
        if (!mondaySet.has(cell.mondayIso)) return false;
        if (usedKeys.has(cell.key)) return false;
        if (!isOperatingCandidate(cell, regularColors, showSaturday)) return false;
        if (cellMatchesSkipColor(cell, skipColorSet)) return false;
        return cellMatchesTrack(
          cell,
          fromInfo.matchAliases,
          wildcard,
          transfer.fromTrack
        );
      })
      .sort(compareCellsReverse);

    let applied = 0;
    for (const cell of candidates) {
      if (applied >= hoursBudget) break;
      if ((cell.textValue ?? '').trim() === writeText) continue;
      usedKeys.add(cell.key);
      updates.push({
        key: cell.key,
        textValue: writeText,
        colorHex: '',
        isBaseExcluded: false,
      });
      applied += 1;
    }
    return applied;
  };

  const consumeCapacity = (term: 1 | 2, fromTrack: string, toTrack: string, applied: number) => {
    if (applied <= 0) return;
    const gk = `${term}\0${fromTrack}`;
    const nk = `${term}\0${toTrack}`;
    giveRemain.set(gk, Math.max(0, (giveRemain.get(gk) ?? 0) - applied));
    needRemain.set(nk, Math.max(0, (needRemain.get(nk) ?? 0) - applied));
  };

  // 1) 체크리스트 항목 우선 — 결과 transferId = 체크리스트 id (완료 표시용)
  if (checklistTransfers && checklistTransfers.length > 0) {
    for (const transfer of checklistTransfers) {
      const gk = `${transfer.term}\0${transfer.fromTrack}`;
      const nk = `${transfer.term}\0${transfer.toTrack}`;
      const capacity = Math.min(
        transfer.hours,
        giveRemain.get(gk) ?? 0,
        needRemain.get(nk) ?? 0
      );
      const applied = applyOneTransfer(transfer, capacity);
      consumeCapacity(transfer.term, transfer.fromTrack, transfer.toTrack, applied);
      results.push({
        transferId: transfer.id,
        term: transfer.term,
        fromTrack: transfer.fromTrack,
        toTrack: transfer.toTrack,
        requested: transfer.hours,
        applied,
      });
    }
  }

  // 2) 남은 라이브 델타 → 추가 이전 (체크리스트에 없는 잔여분)
  const remainDeltas = (term: 1 | 2) =>
    phase2.rows.map((r) => {
      const give = giveRemain.get(`${term}\0${r.trackName}`) ?? 0;
      const need = needRemain.get(`${term}\0${r.trackName}`) ?? 0;
      return { trackName: r.trackName, delta: need - give };
    });
  const leftoverTransfers = [
    ...pairTermTransfers(1, remainDeltas(1)),
    ...pairTermTransfers(2, remainDeltas(2)),
  ];
  for (const transfer of leftoverTransfers) {
    const applied = applyOneTransfer(transfer, transfer.hours);
    consumeCapacity(transfer.term, transfer.fromTrack, transfer.toTrack, applied);
    results.push({
      transferId: transfer.id,
      term: transfer.term,
      fromTrack: transfer.fromTrack,
      toTrack: transfer.toTrack,
      requested: transfer.hours,
      applied,
    });
  }

  // 3) 옵션3: 남은 부족분을 「교과」 행사 칸으로 보충
  let curriculumFillAppliedCount = 0;
  if (fillFromCurriculumEventCells) {
    for (const term of [1, 2] as const) {
      const mondaySet = term === 1 ? term1Set : term2Set;
      for (const row of phase2.rows) {
        const nk = `${term}\0${row.trackName}`;
        let need = needRemain.get(nk) ?? 0;
        if (need <= 0) continue;
        const writeText = (getAliasInfo(row.trackName).writeText || row.trackName).trim();
        if (!writeText) continue;
        const candidates = cells
          .filter((cell) => {
            if (cell.gradeId !== gradeId) return false;
            if (!mondaySet.has(cell.mondayIso)) return false;
            if (usedKeys.has(cell.key)) return false;
            if (!isCurriculumFixedEventCandidate(cell, regularColors, showSaturday)) return false;
            if (cellMatchesSkipColor(cell, skipColorSet)) return false;
            return true;
          })
          .sort(compareCellsReverse);
        let filled = 0;
        for (const cell of candidates) {
          if (filled >= need) break;
          if ((cell.textValue ?? '').trim() === writeText) continue;
          usedKeys.add(cell.key);
          updates.push({
            key: cell.key,
            textValue: writeText,
            colorHex: '',
            isBaseExcluded: false,
          });
          filled += 1;
        }
        if (filled > 0) {
          curriculumFillAppliedCount += filled;
          needRemain.set(nk, Math.max(0, need - filled));
          results.push({
            transferId: `curriculum-fill-t${term}-${row.trackName}`,
            term,
            fromTrack: CURRICULUM_EVENT_CELL_LABEL,
            toTrack: row.trackName,
            requested: need,
            applied: filled,
          });
        }
      }
    }
  }

  const shortfallCount = results.reduce(
    (s, r) => s + Math.max(0, r.requested - r.applied),
    0
  );

  return {
    updates,
    results,
    appliedCount: updates.length,
    shortfallCount,
    curriculumFillAppliedCount,
  };
}
